import axios from "axios";
import { useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { userTableCustomStyles } from "../../../utils/TableCustomStyles";

/**
 * Generic list + add/update/delete panel driven by a module config
 * ({ key, title, endpoint, fields, columns, initialForm }), shared across
 * HR, Asset and Masters CRUD pages. Option loading is caller-owned (passed
 * via `loadOptions`) since option sources differ per module. Extra
 * page-specific toolbar/row actions (e.g. Generate Payroll, Asset Timeline)
 * are injected via the `extraToolbarActions`/`extraRowActions` render props,
 * both of which receive the panel's `reload` (getData) so callers can
 * refresh the table after their own custom action succeeds.
 */
function GenericCrudPanel({ module, baseUrl, loadOptions, extraToolbarActions, extraRowActions, extraRowActionsWidth, filterRows }) {
  const [data, setData] = useState([]);
  const [dataPending, setDataPending] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showAddCanvas, setShowAddCanvas] = useState(false);
  const [showUpdateCanvas, setShowUpdateCanvas] = useState(false);
  const [canvasFullScreen, setCanvasFullScreen] = useState(false);
  const [showAddLoader, setShowAddLoader] = useState(false);
  const [showUpdateLoader, setShowUpdateLoader] = useState(false);
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState(null);
  const [updateId, setUpdateId] = useState("");
  const [addForm, setAddForm] = useState(module.initialForm);
  const [updateForm, setUpdateForm] = useState(module.initialForm);
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState({});

  const hasFile = module.fields.some((field) => field.type === "file");

  const getData = async () => {
    try {
      setDataPending(true);
      setAccessDenied(false);
      const response = await axios.get(`${baseUrl}${module.endpoint}`);
      const rows = response.data || [];
      setData(filterRows ? filterRows(rows) : rows);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(`Error fetching ${module.title.toLowerCase()}.`);
      }
    } finally {
      setDataPending(false);
    }
  };

  const fetchOptions = async () => {
    if (!loadOptions) return;
    try {
      const result = await loadOptions(module);
      setOptions(result || {});
    } catch (err) {
      toast.error("Error loading form options.");
    }
  };

  useEffect(() => {
    setAddForm({ ...module.initialForm });
    setUpdateForm({ ...module.initialForm });
    setUpdateId("");
    setShowAddCanvas(false);
    setShowUpdateCanvas(false);
    setShowDeleteModal(false);
    setDeleteRecordId(null);
    setFilters({});
    getData();
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.key]);

  const validateForm = (form) => {
    const missing = module.fields.filter((field) => field.required).filter((field) => {
      const value = form[field.name];
      if (typeof value === "boolean") return false;
      return value === "" || value === null || value === undefined;
    });

    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map((field) => field.label).join(", ")}`);
      return false;
    }
    return true;
  };

  const buildPayload = (form) => {
    if (hasFile) {
      const payload = new FormData();
      Object.keys(form).forEach((key) => {
        const value = form[key];
        if (value === "" || value === null || value === undefined) return;
        payload.append(key, value);
      });
      return payload;
    }

    const payload = {};
    Object.keys(form).forEach((key) => {
      const value = form[key];
      if (value === "" || value === null || value === undefined) return;
      payload[key] = value;
    });
    return payload;
  };

  const addRecord = async (event) => {
    event.preventDefault();
    if (!validateForm(addForm)) return;

    setShowAddLoader(true);
    try {
      const response = await axios.post(`${baseUrl}${module.endpoint}`, buildPayload(addForm), {
        headers: hasFile ? { "content-type": "multipart/form-data" } : { "content-type": "application/json" },
      });
      if (response.status === 201 || response.status === 200) {
        toast.success(`${module.title} created successfully.`);
        setShowAddCanvas(false);
        setAddForm({ ...module.initialForm });
        getData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || `Error creating ${module.title.toLowerCase()}.`);
    } finally {
      setShowAddLoader(false);
    }
  };

  const handleSetUpdateData = (row) => {
    const prepared = {};
    module.fields.forEach((field) => {
      const value = row[field.name];
      if (typeof value === "object" && value !== null && value.id) {
        prepared[field.name] = value.id;
      } else if (value === null || value === undefined) {
        prepared[field.name] = "";
      } else {
        prepared[field.name] = value;
      }
    });
    setUpdateId(row.id);
    setUpdateForm(prepared);
    setShowUpdateCanvas(true);
    setShowAddCanvas(false);
    setCanvasFullScreen(false);
  };

  const updateRecord = async (event) => {
    event.preventDefault();
    if (!validateForm(updateForm)) return;

    setShowUpdateLoader(true);
    try {
      const response = await axios.put(`${baseUrl}${module.endpoint}${updateId}/`, buildPayload(updateForm), {
        headers: hasFile ? { "content-type": "multipart/form-data" } : { "content-type": "application/json" },
      });
      if (response.status === 200 || response.status === 201) {
        toast.success(`${module.title} updated successfully.`);
        setShowUpdateCanvas(false);
        setUpdateForm({ ...module.initialForm });
        setUpdateId("");
        getData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || `Error updating ${module.title.toLowerCase()}.`);
    } finally {
      setShowUpdateLoader(false);
    }
  };

  const deleteRecord = async () => {
    if (!deleteRecordId) return;

    setShowDeleteLoader(true);
    try {
      await axios.delete(`${baseUrl}${module.endpoint}${deleteRecordId}/`);
      toast.success(`${module.title} deleted successfully.`);
      setShowDeleteModal(false);
      setDeleteRecordId(null);
      getData();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || `Error deleting ${module.title.toLowerCase()}.`);
    } finally {
      setShowDeleteLoader(false);
    }
  };

  const renderField = (field, form, setForm) => {
    const updateField = (event) => {
      const value = field.type === "checkbox" ? event.target.checked : event.target.value;
      setForm((prev) => {
        const nextForm = { ...prev, [field.name]: value };
        return module.calculateForm ? module.calculateForm(nextForm) : nextForm;
      });
    };

    if (field.type === "textarea") {
      return (
        <Form.Control
          as="textarea"
          rows={3}
          value={form[field.name] ?? ""}
          onChange={updateField}
          disabled={field.disabled}
        />
      );
    }

    if (field.type === "select") {
      return (
        <Form.Select
          value={form[field.name] ?? ""}
          onChange={updateField}
          disabled={field.disabled}
        >
          <option value="">Select {field.label}</option>
          {(options[field.optionsKey] || []).map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (field.type === "checkbox") {
      return (
        <Form.Check
          type="checkbox"
          checked={Boolean(form[field.name])}
          onChange={updateField}
          label={field.label}
          disabled={field.disabled}
        />
      );
    }

    if (field.type === "file") {
      return (
        <Form.Control
          type="file"
          onChange={(event) => {
            const selectedFile = event.target.files?.[0] || null;
            if (selectedFile && field.maxSizeMB && selectedFile.size > field.maxSizeMB * 1024 * 1024) {
              toast.error(`${field.label} must be ${field.maxSizeMB}MB or smaller.`);
              event.target.value = "";
              return;
            }
            setForm((prev) => ({ ...prev, [field.name]: selectedFile }));
          }}
          disabled={field.disabled}
        />
      );
    }

    return (
      <Form.Control
        type={field.type || "text"}
        step={field.step}
        value={form[field.name] ?? ""}
        onChange={updateField}
        disabled={field.disabled}
      />
    );
  };

  // const tableColumns = [
  //   ...module.columns,
  //   {
  //     name: "Action",
  //     right: true,
  //     selector: (row) => (
  //       <div>
  //         {extraRowActions ? extraRowActions(row, module, getData) : null}
  //         <Button className="btn btn-sm btn-inox-green border-0" title="Update" onClick={() => handleSetUpdateData(row)}>
  //           <i className="fa fa-edit"></i>
  //         </Button>
  //         <Button className="btn btn-sm bg-danger border-0 mx-2" title="Delete" onClick={() => { setDeleteRecordId(row.id); setShowDeleteModal(true); }}>
  //           <i className="fa fa-trash"></i>
  //         </Button>
  //       </div>
  //     ),
  //   },
  // ];

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  const filteredData = data.filter((row) => {
    return module.columns.every((column) => {
      if (!column.filterKey) {
        return true;
      }

      const filterValue = (filters[column.filterKey] || "")
        .toString()
        .trim()
        .toLowerCase();

      if (!filterValue) {
        return true;
      }

      const value = column.filterValue
        ? column.filterValue(row)
        : row[column.filterKey];

      return String(value ?? "")
        .toLowerCase()
        .includes(filterValue);
    });
  });

  const tableColumns = [
    ...module.columns.map((column) => ({
      ...column,

      name: column.filterKey ? (
        <div
          className="form-group my-2"
          style={{ minWidth: column.filterWidth || "120px" }}
        >
          <label className="mb-1">
            {typeof column.name === "string"
              ? column.name
              : "Filter"}
          </label>

          <input
            type="text"
            className="form form-control form-control-sm"
            style={{
              width: column.filterWidth || "120px",
            }}
            value={filters[column.filterKey] || ""}
            placeholder={
              column.filterPlaceholder ||
              `Search ${typeof column.name === "string"
                ? column.name
                : ""
              }`
            }
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onChange={(e) =>
              handleFilterChange(
                column.filterKey,
                e.target.value
              )
            }
          />
        </div>
      ) : (
        column.name
      ),
    })),

    {
      name: "Action",
      right: true,
      width: extraRowActionsWidth || "200px",
      selector: (row) => (
        <div>
          {extraRowActions
            ? extraRowActions(row, module, getData)
            : null}

          <Button
            className="btn btn-sm btn-inox-green border-0"
            title="Update"
            onClick={() => handleSetUpdateData(row)}
          >
            <i className="fa fa-edit"></i>
          </Button>

          <Button
            className="btn btn-sm bg-danger border-0 mx-2"
            title="Delete"
            onClick={() => {
              setDeleteRecordId(row.id);
              setShowDeleteModal(true);
            }}
          >
            <i className="fa fa-trash"></i>
          </Button>
        </div>
      ),
    },
  ];

  if (accessDenied) {
    return (
      <div className="text-center py-5">
        <i className="fa fa-lock fa-2x text-danger mb-3"></i>
        <p>Access denied. You do not have permission to view {module.title.toLowerCase()}.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">{module.title}</h5>
        <div>
          {extraToolbarActions ? extraToolbarActions(module, getData) : null}
          <Button className="btn btn-sm btn-inox-green border-0 px-3" onClick={() => { setAddForm({ ...module.initialForm }); setShowAddCanvas(true); setShowUpdateCanvas(false); setCanvasFullScreen(false); }}>
            <i className="fa fa-plus me-2"></i>
            Add {module.title}
          </Button>
        </div>
      </div>

      <DataTable
        columns={tableColumns}
        data={filteredData}
        progressPending={dataPending}
        progressComponent={<Spinner className="mx-5 text-inox-green table-spinner" as="span" animation="border" size="lg" role="status" aria-hidden="true" />}
        noDataComponent={<p className="text-center table-no-data">No Data Found</p>}
        pagination
        highlightOnHover
        fixedHeader
        persistTableHead
        customStyles={userTableCustomStyles}
        dense
      />

      <div className={`offcanvas-backdrop ${showAddCanvas || showUpdateCanvas ? "show" : ""}`} style={{ display: showAddCanvas || showUpdateCanvas ? "block" : "none" }} />

      <div
        className={`offcanvas offcanvas-bottom ${showAddCanvas || showUpdateCanvas ? "show" : ""}`}
        tabIndex="-1"
        style={{ visibility: showAddCanvas || showUpdateCanvas ? "visible" : "hidden", height: canvasFullScreen ? "100%" : "60%", overflowY: "auto" }}
      >
        <div className="offcanvas-header border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
          <h5 className="offcanvas-title mb-0">{showUpdateCanvas ? `Update ${module.title}` : `Add ${module.title}`}</h5>
          <div className="float-right">
            <Button className="btn btn-sm btn-inox-green border-0 mx-2" onClick={() => { setCanvasFullScreen(!canvasFullScreen); }}>
              {canvasFullScreen ? <i className="fa fa-window-minimize text-white"></i> : <i className="fa fa-window-maximize text-white"></i>}
            </Button>
            <Button className="btn btn-danger btn-sm" size="sm" onClick={() => { setShowAddCanvas(false); setShowUpdateCanvas(false); setCanvasFullScreen(false); }}>
              <i className="fa fa-times text-white"></i>
            </Button>
          </div>
        </div>
        <div className="offcanvas-body px-4 py-3">
          <Form onSubmit={showUpdateCanvas ? updateRecord : addRecord}>
            <div className="row">
              {module.fields.filter((field) => field.type !== "hidden").map((field) => (
                <div className="col-md-6 mb-3" key={field.name}>
                  <Form.Label>{field.label}{field.required ? <span className="text-danger"> *</span> : null}</Form.Label>
                  {renderField(field, showUpdateCanvas ? updateForm : addForm, showUpdateCanvas ? setUpdateForm : setAddForm)}
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button variant="secondary" onClick={() => { setShowAddCanvas(false); setShowUpdateCanvas(false); setCanvasFullScreen(false); }}>
                Close
              </Button>
              <Button type="submit" className="btn-inox-green border-0">
                {showUpdateCanvas ? (showUpdateLoader ? <Spinner as="span" animation="border" size="sm" /> : "Update") : (showAddLoader ? <Spinner as="span" animation="border" size="sm" /> : "Create")}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <div className="modal fade show" style={{ display: showDeleteModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete</h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">Do you want to delete this record?</div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Close</Button>
              <Button variant="danger" onClick={deleteRecord}>
                {showDeleteLoader ? <Spinner as="span" animation="border" size="sm" /> : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GenericCrudPanel;
